import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TipTapLink from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';

import {
  BookEquationBlock,
  BookEquationInline,
} from './BookEquation';
import EquationPanel from './EquationPanel';
import LinkPanel from './LinkPanel';
import {
  BookSubscript,
  BookSuperscript,
  toggleBookScript,
} from './BookScriptMarks';
import { sanitizeTableCellHtml } from './tableCellContent';
import './ContentLayout.css';


const CellTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style') || null,
        renderHTML: (attributes) =>
          attributes.style ? { style: attributes.style } : {},
      },
    };
  },
});

const COLORS = [
  '#000000',
  '#374151',
  '#FF0000',
  '#C00000',
  '#00AA00',
  '#0000FF',
  '#FFA500',
  '#800080',
];

const FONTS = [
  ['Arial', 'Arial, sans-serif'],
  ['Times New Roman', 'Times New Roman, serif'],
  ['Calibri', 'Calibri, sans-serif'],
  ['Verdana', 'Verdana, sans-serif'],
  ['Georgia', 'Georgia, serif'],
  ['Courier New', "'Courier New', Courier, monospace"],
  ['Impact', 'Impact, Haettenschweiler, Arial Black, sans-serif'],
  ['Comic Sans MS', "'Comic Sans MS', 'Comic Sans', cursive"],
];

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32];

const parseStyle = (style) => {
  const declarations = {};
  String(style || '')
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) return;
      declarations[declaration.slice(0, separator).trim().toLowerCase()] =
        declaration.slice(separator + 1).trim();
    });
  return declarations;
};

const stringifyStyle = (declarations) =>
  Object.entries(declarations)
    .filter(([property, value]) => property && value)
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ');

const TableCellRichEditor = ({
  html = '',
  onChange,
  ariaLabel = 'Contenu enrichi de la cellule',
}) => {
  const [linkPanel, setLinkPanel] = useState(null);
  const [showEquationPanel, setShowEquationPanel] = useState(false);
  const [activeEquation, setActiveEquation] = useState(null);
  const savedLinkSelectionRef = useRef(null);
  const savedEquationSelectionRef = useRef(null);
  const editorRef = useRef(null);

  const openExistingEquation = useCallback((equation) => {
    setActiveEquation(equation);
    setShowEquationPanel(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      BookSubscript,
      BookSuperscript,
      TipTapLink.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        protocols: ['http', 'https', 'mailto'],
        HTMLAttributes: {
          target: null,
          rel: null,
          class: null,
        },
      }),
      CellTextStyle,
      FontFamily,
      BookEquationInline.configure({ onEditEquation: openExistingEquation }),
      BookEquationBlock.configure({ onEditEquation: openExistingEquation }),
    ],
    content: sanitizeTableCellHtml(html) || '<p></p>',
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        class: 'book-table-cell-rich-editor__surface',
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange?.(sanitizeTableCellHtml(currentEditor.getHTML()));
    },
  });
  editorRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    const safeHtml = sanitizeTableCellHtml(html) || '<p></p>';
    if (sanitizeTableCellHtml(editor.getHTML()) !== safeHtml) {
      editor.commands.setContent(safeHtml, false);
    }
  }, [editor, html]);

  const applyTextStyle = useCallback((patch) => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;
    const attributes = currentEditor.getAttributes('textStyle') || {};
    const declarations = parseStyle(attributes.style);
    Object.entries(patch).forEach(([property, value]) => {
      if (value) declarations[property] = value;
      else delete declarations[property];
    });
    currentEditor
      .chain()
      .focus()
      .setMark('textStyle', {
        ...attributes,
        style: stringifyStyle(declarations),
      })
      .run();
  }, []);

  const openLinkPanel = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;
    const editing = currentEditor.isActive('link');
    if (editing) currentEditor.commands.extendMarkRange('link');
    const { from, to } = currentEditor.state.selection;
    savedLinkSelectionRef.current = { from, to };
    setLinkPanel({
      initialText:
        from === to ? '' : currentEditor.state.doc.textBetween(from, to, ''),
      initialHref: editing ? currentEditor.getAttributes('link').href || '' : '',
      editing,
    });
  }, []);

  const openClickedLink = useCallback((event) => {
    const target = event.target instanceof Element
      ? event.target.closest('a[href]')
      : null;
    const currentEditor = editorRef.current;
    if (!target || !currentEditor?.view.dom.contains(target)) return;
    event.preventDefault();
    const from = currentEditor.view.posAtDOM(target, 0);
    const text = target.textContent || '';
    savedLinkSelectionRef.current = { from, to: from + text.length };
    setLinkPanel({
      initialText: text,
      initialHref: target.getAttribute('href') || '',
      editing: true,
    });
  }, []);

  const confirmLink = useCallback(({ text, href, target, rel }) => {
    const currentEditor = editorRef.current;
    const selection = savedLinkSelectionRef.current;
    if (!currentEditor || !selection) return;
    currentEditor
      .chain()
      .insertContentAt(selection, {
        type: 'text',
        text,
        marks: [
          {
            type: 'link',
            attrs: { href, target: target || null, rel: rel || null },
          },
        ],
      })
      .focus(null, { scrollIntoView: false })
      .run();
    savedLinkSelectionRef.current = null;
    setLinkPanel(null);
  }, []);

  const removeLink = useCallback(() => {
    const currentEditor = editorRef.current;
    const selection = savedLinkSelectionRef.current;
    if (!currentEditor || !selection) return;
    currentEditor
      .chain()
      .setTextSelection(selection)
      .unsetLink()
      .focus(null, { scrollIntoView: false })
      .run();
    savedLinkSelectionRef.current = null;
    setLinkPanel(null);
  }, []);

  const cancelLink = useCallback(() => {
    const currentEditor = editorRef.current;
    const selection = savedLinkSelectionRef.current;
    setLinkPanel(null);
    window.setTimeout(() => {
      if (!currentEditor) return;
      if (selection) currentEditor.commands.setTextSelection(selection);
      currentEditor.view.dom.focus();
    }, 0);
  }, []);

  const openEquationPanel = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;
    const { from, to } = currentEditor.state.selection;
    savedEquationSelectionRef.current = { from, to };
    setActiveEquation(null);
    setShowEquationPanel(true);
  }, []);

  const closeEquationPanel = useCallback(() => {
    setShowEquationPanel(false);
    setActiveEquation(null);
  }, []);

  const confirmEquation = useCallback((equation) => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;
    if (Number.isInteger(activeEquation?.pos)) {
      currentEditor.commands.updateBookEquation({
        pos: activeEquation.pos,
        latex: equation.latex,
        display: equation.display,
      });
    } else {
      const chain = currentEditor.chain().focus();
      if (savedEquationSelectionRef.current) {
        chain.setTextSelection(savedEquationSelectionRef.current);
      }
      chain.insertBookEquation(equation).run();
    }
    closeEquationPanel();
  }, [activeEquation, closeEquationPanel]);

  const deleteEquation = useCallback(() => {
    const currentEditor = editorRef.current;
    if (currentEditor && Number.isInteger(activeEquation?.pos)) {
      currentEditor.commands.deleteBookEquation({ pos: activeEquation.pos });
    }
    closeEquationPanel();
  }, [activeEquation, closeEquationPanel]);

  const cancelEquation = useCallback(() => {
    const currentEditor = editorRef.current;
    const selection = savedEquationSelectionRef.current;
    closeEquationPanel();
    window.setTimeout(() => {
      if (!currentEditor) return;
      if (!activeEquation && selection) {
        currentEditor.commands.setTextSelection(selection);
      }
      currentEditor.view.dom.focus();
    }, 0);
  }, [activeEquation, closeEquationPanel]);

  if (!editor) return null;

  return (
    <div className="book-table-cell-rich-editor">
      <div role="toolbar" aria-label="Outils de la cellule" className="book-table-cell-rich-editor__toolbar">
        <button
          type="button"
          aria-label="Gras"
          aria-pressed={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Gras
        </button>
        <button
          type="button"
          aria-label="Italique"
          aria-pressed={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italique
        </button>
        <button
          type="button"
          aria-label="Souligné"
          aria-pressed={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          Souligné
        </button>
        <button
          type="button"
          aria-label="Indice X₂"
          aria-pressed={editor.isActive('subscript')}
          onClick={() => toggleBookScript(editor, 'subscript')}
        >
          X₂
        </button>
        <button
          type="button"
          aria-label="Exposant X²"
          aria-pressed={editor.isActive('superscript')}
          onClick={() => toggleBookScript(editor, 'superscript')}
        >
          X²
        </button>
        <button
          type="button"
          aria-label="Liste à puces"
          aria-pressed={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Puces
        </button>
        <button
          type="button"
          aria-label="Liste numérotée"
          aria-pressed={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          Numéros
        </button>
        <button type="button" aria-label="Lien" onClick={openLinkPanel}>
          Lien
        </button>
        <button type="button" aria-label="Équation" onClick={openEquationPanel}>
          Équation
        </button>
        <label className="book-table-cell-rich-editor__select">
          <span className="sr-only">Police de caractères</span>
          <select
            aria-label="Police de caractères"
            value=""
            onChange={(event) => {
              if (event.target.value) {
                applyTextStyle({ 'font-family': event.target.value });
              }
            }}
          >
            <option value="">Police</option>
            {FONTS.map(([label, value]) => (
              <option key={label} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="book-table-cell-rich-editor__select">
          <span className="sr-only">Taille du texte</span>
          <select
            aria-label="Taille du texte"
            value=""
            onChange={(event) => {
              if (event.target.value) {
                applyTextStyle({ 'font-size': `${event.target.value}px` });
              }
            }}
          >
            <option value="">Taille</option>
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>{size} px</option>
            ))}
          </select>
        </label>
        <label className="book-table-cell-rich-editor__color">
          <span>Couleur</span>
          <input
            type="color"
            aria-label="Couleur du texte"
            defaultValue="#000000"
            onChange={(event) => applyTextStyle({ color: event.target.value })}
          />
        </label>
        <div className="book-table-cell-rich-editor__palette" aria-label="Couleurs rapides du texte">
          {COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Couleur ${color}`}
              style={{ backgroundColor: color }}
              onClick={() => applyTextStyle({ color })}
            />
          ))}
        </div>
      </div>

      {linkPanel && (
        <LinkPanel
          open
          initialText={linkPanel.initialText}
          initialHref={linkPanel.initialHref}
          editing={linkPanel.editing}
          onConfirm={confirmLink}
          onRemove={removeLink}
          onCancel={cancelLink}
        />
      )}
      {showEquationPanel && (
        <EquationPanel
          open
          initialEquation={activeEquation}
          onConfirm={confirmEquation}
          onDelete={deleteEquation}
          onCancel={cancelEquation}
        />
      )}

      <div onClick={openClickedLink}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TableCellRichEditor;
